
import { useState, useRef, useEffect } from "react"
import useTranslation from "../hooks/useTranslation"

const LanguageSwitcher = () => {
  const { t, currentLanguage, changeLanguage } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Language options with flags
  const languageOptions = [
    { code: "en" as const, name: t('language.english'), flag: "/en.png" },
    { code: "ar" as const, name: t('language.arabic'), flag: "/ar.png" },
  ]

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Change language
  const handleLanguageChange = (langCode: "ar" | "en") => {
    changeLanguage(langCode)
    setIsOpen(false)
  }

  // Get current language
  const currentLanguageOption = languageOptions.find((option) => option.code === currentLanguage)

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white hover:bg-gray-50 transition-colors"
      >
        {/* Flag image for current language */}
        <div className="w-6 h-4 overflow-hidden flex-shrink-0 flex items-center justify-center">
          {currentLanguageOption && (
            <>
              <img
                src={currentLanguageOption.code === "ar" ? "/ar.png" : "/en.png"}
                alt={`${currentLanguageOption.name} flag`}
                className="w-full h-full object-contain"
                onError={(e) => {
                  const target = e.target as HTMLImageElement
                  target.style.display = "none"
                  const nextSibling = target.nextSibling as HTMLElement
                  if (nextSibling) nextSibling.style.display = "block"
                }}
              />
              <span role="img" aria-label={`${currentLanguageOption.name} flag`} className="text-lg hidden">
                {currentLanguageOption.code === "ar" ? "🇸🇦" : "🇺🇸"}
              </span>
            </>
          )}
        </div>

        {/* Current language name */}
        <span className="text-sm font-medium">{currentLanguageOption?.name}</span>

        {/* Arrow to show this is a dropdown */}
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10">
          <ul className="py-1">
            {languageOptions.map((option) => (
              <li key={option.code}>
                <button
                  onClick={() => handleLanguageChange(option.code)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 ${
                    currentLanguage === option.code ? "bg-gray-50 font-semibold" : ""
                  }`}
                >
                  {/* Flag image */}
                  <div className="w-6 h-4 overflow-hidden flex-shrink-0">
                    <img
                      src={option.code === "ar" ? "/ar.png" : "/en.png"}
                      alt={`${option.name} flag`}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = "none"
                        const nextSibling = target.nextSibling as HTMLElement
                        if (nextSibling) nextSibling.style.display = "block"
                      }}
                    />
                    <span role="img" aria-label={`${option.name} flag`} className="text-lg hidden">
                      {option.code === "ar" ? "🇸🇦" : "🇺🇸"}
                    </span>
                  </div>

                  {/* Language name */}
                  <span>{option.name}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export default LanguageSwitcher
